import { useMemo } from 'react'
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import { useTheme } from '../context/ThemeContext'

function ParetoChart({ data = [], maxBars = 50 }) {
    const { theme } = useTheme()

    const chartData = useMemo(() => {
        if (data.length === 0) return { bars: [], totalOil: 0, wellCount: 0 }

        const totalOil = data.reduce((sum, d) => sum + d.oil, 0)
        const topWells = data.slice(0, maxBars)
        let cumulative = 0

        const bars = topWells.map(d => {
            cumulative += d.oil
            return {
                name: d.name,
                oil: d.oil,
                cumPct: (cumulative / totalOil) * 100
            }
        })

        return { bars, totalOil, wellCount: data.length }
    }, [data, maxBars])

    if (chartData.bars.length === 0) {
        return (
            <div className="h-80 flex items-center justify-center text-gray-400">
                No production concentration data
            </div>
        )
    }

    const isDark = theme === 'dark'

    return (
        <div>
            <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {chartData.wellCount} producing wells | Top {Math.min(maxBars, chartData.wellCount)} shown
            </p>
            <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData.bars}
                        margin={{ top: 5, right: 40, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
                        <XAxis
                            dataKey="name"
                            tick={false}
                            stroke={isDark ? '#475569' : '#d1d5db'}
                            label={{
                                value: `Top ${Math.min(maxBars, chartData.wellCount)} Wells`,
                                position: 'insideBottom',
                                offset: 0,
                                style: { fontSize: 12, fill: isDark ? '#94a3b8' : '#6b7280' }
                            }}
                        />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#6b7280' }}
                            tickLine={{ stroke: isDark ? '#475569' : '#d1d5db' }}
                            stroke={isDark ? '#475569' : '#d1d5db'}
                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                            label={{ value: 'BBL', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: isDark ? '#cbd5e1' : '#374151' } }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 100]}
                            tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#6b7280' }}
                            tickLine={{ stroke: isDark ? '#475569' : '#d1d5db' }}
                            stroke={isDark ? '#475569' : '#d1d5db'}
                            tickFormatter={(v) => `${v}%`}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                                color: isDark ? '#e2e8f0' : '#111827'
                            }}
                            formatter={(value, name) => {
                                if (name === 'oil') return [`${value.toLocaleString()} BBL`, 'Daily Oil']
                                if (name === 'cumPct') return [`${value.toFixed(1)}%`, 'Cumulative']
                                return [value, name]
                            }}
                            labelFormatter={(label) => label}
                        />
                        <Bar
                            yAxisId="left"
                            dataKey="oil"
                            fill="#3b82f6"
                            fillOpacity={0.8}
                            radius={[2, 2, 0, 0]}
                        />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="cumPct"
                            stroke="#f59e0b"
                            strokeWidth={2.5}
                            dot={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default ParetoChart
